import { useEffect, useState } from "react";
import AppShell from "../components/ui/AppShell";
import { useCart } from "../context/CartContext";
import { supabase } from "../lib/supabase";

function Materials() {
  const { addToCart } = useCart();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantityMap, setQuantityMap] = useState({});

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("materials")
        .select(
          `
          *,
          shops (
            shop_name,
            city,
            state,
            country,
            pincode
          )
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;

      const materialsWithImages = await Promise.all(
        (data || []).map(async (item) => {
          let resolvedImageUrl = item.image_url || "";

          if (!resolvedImageUrl && item.image_file_path) {
            const { data: publicUrlData } = supabase.storage
              .from("shop-assets")
              .getPublicUrl(item.image_file_path);

            resolvedImageUrl = publicUrlData?.publicUrl || "";
          }

          return {
            ...item,
            resolvedImageUrl,
          };
        })
      );

      setMaterials(materialsWithImages);
    } catch (error) {
      console.error("Fetch materials error:", error);
      alert(error.message || "Failed to load materials.");
    } finally {
      setLoading(false);
    }
  };

  const getQuantity = (id) => quantityMap[id] || 1;

  const updateQuantity = (id, value, maxQty) => {
    const parsed = Number(value);

    if (!parsed || parsed < 1) {
      setQuantityMap((prev) => ({
        ...prev,
        [id]: 1,
      }));
      return;
    }

    const safeQty = Math.min(parsed, maxQty || parsed);

    setQuantityMap((prev) => ({
      ...prev,
      [id]: safeQty,
    }));
  };

  const handleIncrease = (id, maxQty) => {
    const current = getQuantity(id);
    if (current >= maxQty) return;

    setQuantityMap((prev) => ({
      ...prev,
      [id]: current + 1,
    }));
  };

  const handleDecrease = (id) => {
    const current = getQuantity(id);
    if (current <= 1) return;

    setQuantityMap((prev) => ({
      ...prev,
      [id]: current - 1,
    }));
  };

  const getDisplayMaterialTitle = (item) => {
    if (!item?.material_name) return "Material";
    return item.material_name.replace(/\s*\[[A-Z0-9.-]+\]\s*$/, "");
  };

  const handleAddToCart = (item) => {
    const availableQty = Number(item.quantity || 0);
    const quantity = getQuantity(item.id);

    if (availableQty <= 0) {
      alert("This material is currently out of stock.");
      return;
    }

    if (quantity > availableQty) {
      alert("Selected quantity exceeds available stock.");
      return;
    }

    const unitPrice = Number(
      item.customer_offer_price || item.offered_price || item.price || 0
    );
    const finalPrice = unitPrice * quantity;

    addToCart({
      type: "material",
      materialId: item.id,
      title: getDisplayMaterialTitle(item),
      description: item.category || "",
      basePrice: unitPrice,
      finalPrice,
      quantity,
      unit: item.unit || "unit",
      materialType: item.category || "general",
      materialLengthCm: 30,
      distanceKm: 5,
      vehicleType: "bike",
      shopName: item.shops?.shop_name || "",
      city: item.shops?.city || "",
      pincode: item.shops?.pincode || "",
      deliveryETA: "Calculated at checkout",
      imageUrl: item.resolvedImageUrl || "",
      availableQty,
    });

    alert(`${getDisplayMaterialTitle(item)} added to cart`);
  };

  return (
    <AppShell maxWidth="1240px">
      <div style={heroCard}>
        <h1 style={{ marginTop: 0 }} className="materials__title">
          Materials
        </h1>
        <p
          style={{ color: "#d1d1d1", margin: 0, lineHeight: "1.8" }}
          className="materials__hero-text"
        >
          Browse inventory uploaded by shop owners. Add quantity, compare
          pricing, and proceed to checkout for delivery and slot-based dispatch.
        </p>
      </div>

      {loading ? (
        <div style={{ marginTop: "24px" }}>
          <p>Loading materials...</p>
        </div>
      ) : materials.length === 0 ? (
        <div style={{ ...emptyBox, marginTop: "24px" }}>
          <p>No materials available right now.</p>
        </div>
      ) : (
        <div style={grid} className="materials__grid">
          {materials.map((item) => {
            const quantity = getQuantity(item.id);
            const unitPrice = Number(
              item.customer_offer_price || item.offered_price || item.price || 0
            );
            const mrpPrice = Number(
              item.customer_mrp_price || item.mrp_price || unitPrice || 0
            );
            const availableQty = Number(item.quantity || 0);
            const subtotal = unitPrice * quantity;
            const outOfStock = availableQty <= 0;

            return (
              <div key={item.id} style={card} className="materials__card">
                <div style={imageWrap}>
                  {item.resolvedImageUrl ? (
                    <img
                      src={item.resolvedImageUrl}
                      alt={getDisplayMaterialTitle(item)}
                      style={image}
                    />
                  ) : (
                    <div style={imagePlaceholder}>No Image</div>
                  )}
                </div>

                <div style={{ marginTop: "16px" }}>
                  <div style={topRow} className="materials__top-row">
                    <h3 style={{ marginTop: 0, marginBottom: "8px" }}>
                      {getDisplayMaterialTitle(item)}
                    </h3>

                    {item.master_sku_code ? (
                      <div style={skuPill}>{item.master_sku_code}</div>
                    ) : null}

                    <div
                      style={{
                        ...stockPill,
                        background: outOfStock ? "#2a1111" : "#0f2416",
                        color: outOfStock ? "#ff9a9a" : "#7CFC00",
                      }}
                    >
                      {outOfStock ? "Out of Stock" : "Available"}
                    </div>
                  </div>

                  <p style={muted}>
                    <strong>Category:</strong> {item.category || "N/A"}
                  </p>
                  <p style={muted}>
                    <strong>Offered Price:</strong> Rs {unitPrice}
                  </p>
                  <p style={muted}>
                    <strong>MRP:</strong> Rs {mrpPrice}
                  </p>
                  <p style={muted}>
                    <strong>Available Qty:</strong> {availableQty}
                  </p>
                  <p style={muted}>
                    <strong>Unit:</strong> {item.unit || "unit"}
                  </p>
                  <p style={muted}>
                    <strong>Shop:</strong> {item.shops?.shop_name || "N/A"}
                  </p>
                  <p style={muted}>
                    <strong>City:</strong> {item.shops?.city || "N/A"}
                  </p>

                  {item.shops?.pincode && (
                    <p style={muted}>
                      <strong>Pincode:</strong> {item.shops.pincode}
                    </p>
                  )}

                  <div style={{ marginTop: "16px" }}>
                    <label style={{ fontWeight: "bold" }}>Quantity</label>

                    <div style={qtyRow} className="materials__qty-row">
                      <button
                        onClick={() => handleDecrease(item.id)}
                        style={qtyBtn}
                        disabled={outOfStock}
                      >
                        -
                      </button>

                      <input
                        type="number"
                        min="1"
                        max={availableQty || 1}
                        value={quantity}
                        onChange={(e) =>
                          updateQuantity(item.id, e.target.value, availableQty)
                        }
                        style={input}
                        className="materials__qty-input"
                        disabled={outOfStock}
                      />

                      <button
                        onClick={() => handleIncrease(item.id, availableQty)}
                        style={qtyBtn}
                        disabled={outOfStock}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div style={subtotalBox}>
                    <p style={{ margin: 0 }}>
                      <strong>Subtotal:</strong> Rs {subtotal}
                    </p>
                  </div>

                  <button
                    onClick={() => handleAddToCart(item)}
                    style={{
                      ...solidBtn,
                      opacity: outOfStock ? 0.6 : 1,
                      cursor: outOfStock ? "not-allowed" : "pointer",
                    }}
                    disabled={outOfStock}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}

const heroCard = {
  background: "linear-gradient(135deg, #111, #191919)",
  padding: "24px",
  borderRadius: "18px",
  border: "1px solid #2a2a2a",
};

const emptyBox = {
  background: "#111",
  padding: "20px",
  borderRadius: "14px",
  border: "1px solid #2a2a2a",
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: "18px",
  marginTop: "24px",
};

const card = {
  background: "linear-gradient(180deg, #111, #0b0b0b)",
  padding: "22px",
  borderRadius: "18px",
  border: "1px solid #2a2a2a",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 12px 30px rgba(0,0,0,0.2)",
};

const imageWrap = {
  width: "100%",
  height: "220px",
  borderRadius: "14px",
  overflow: "hidden",
  background: "#0b0b0b",
  border: "1px solid #222",
};

const image = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const imagePlaceholder = {
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#777",
  fontWeight: "bold",
};

const topRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
};

const stockPill = {
  padding: "8px 12px",
  borderRadius: "999px",
  fontWeight: "bold",
  fontSize: "12px",
};

const skuPill = {
  padding: "8px 12px",
  borderRadius: "999px",
  fontWeight: "bold",
  fontSize: "12px",
  background: "#101827",
  color: "#9fc3ff",
};

const qtyRow = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginTop: "8px",
};

const qtyBtn = {
  width: "40px",
  height: "40px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  cursor: "pointer",
  fontSize: "18px",
};

const input = {
  padding: "10px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  width: "90px",
  textAlign: "center",
};

const subtotalBox = {
  marginTop: "16px",
  padding: "14px",
  borderRadius: "12px",
  background: "#0b0b0b",
  border: "1px solid #222",
};

const solidBtn = {
  marginTop: "16px",
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  fontWeight: "bold",
};

const muted = {
  color: "#d1d1d1",
  margin: "6px 0",
};

export default Materials;
